import {
    collection,
    doc,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

export interface Prompt {
    id: string;
    title: string;
    content: string;
    type: 'public' | 'private';
    ownerId: string;
    ownerName?: string;
    useProductContext?: boolean;
    createdAt: Date;
    updatedAt: Date;
}

export interface CreatePromptData {
    title: string;
    content: string;
    type: 'public' | 'private';
    useProductContext?: boolean;
}

export interface UpdatePromptData {
    title?: string;
    content?: string;
    type?: 'public' | 'private';
    useProductContext?: boolean;
}

const PROMPTS_COLLECTION = 'prompts';

/**
 * Get all prompts visible to the user (public + own private prompts)
 */
export async function getPrompts(userId: string): Promise<Prompt[]> {
    try {
        // Query for public prompts OR prompts owned by the user
        const promptsRef = collection(db, PROMPTS_COLLECTION);

        // Firestore doesn't support OR queries across different fields well,
        // so we'll fetch all and filter client-side for simplicity
        const snapshot = await getDocs(promptsRef);

        const prompts: Prompt[] = [];

        snapshot.forEach((docSnap) => {
            const data = docSnap.data();
            // Include if public OR if owned by user
            if (data.type === 'public' || data.ownerId === userId) {
                prompts.push({
                    id: docSnap.id,
                    title: data.title || '',
                    content: data.content || '',
                    type: data.type || 'private',
                    ownerId: data.ownerId || '',
                    ownerName: data.ownerName,
                    useProductContext: data.useProductContext ?? true,
                    createdAt: data.createdAt?.toDate() || new Date(),
                    updatedAt: data.updatedAt?.toDate() || new Date(),
                });
            }
        });

        // Sort by updatedAt descending (most recent first)
        prompts.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());

        return prompts;
    } catch (error) {
        console.error('Error fetching prompts:', error);
        throw error;
    }
}

/**
 * Create a new prompt
 */
export async function createPrompt(
    data: CreatePromptData,
    userId: string,
    userName: string
): Promise<string> {
    try {
        const now = Timestamp.now();

        const docRef = await addDoc(collection(db, PROMPTS_COLLECTION), {
            title: data.title,
            content: data.content,
            type: data.type,
            useProductContext: data.useProductContext ?? true,
            ownerId: userId,
            ownerName: userName,
            createdAt: now,
            updatedAt: now,
        });

        return docRef.id;
    } catch (error) {
        console.error('Error creating prompt:', error);
        throw error;
    }
}

/**
 * Update an existing prompt
 */
export async function updatePrompt(
    promptId: string,
    data: UpdatePromptData
): Promise<void> {
    try {
        const promptRef = doc(db, PROMPTS_COLLECTION, promptId);

        await updateDoc(promptRef, {
            ...data,
            updatedAt: Timestamp.now(),
        });
    } catch (error) {
        console.error('Error updating prompt:', error);
        throw error;
    }
}

/**
 * Delete a prompt
 */
export async function deletePrompt(promptId: string): Promise<void> {
    try {
        const promptRef = doc(db, PROMPTS_COLLECTION, promptId);
        await deleteDoc(promptRef);
    } catch (error) {
        console.error('Error deleting prompt:', error);
        throw error;
    }
}
